/** Shared styles & helpers for Shipment Monitoring column defs. */

import dayjs from 'dayjs';

export const shipmentMonitoringCellTypographySx = {
    fontSize: 10,
    whiteSpace: 'pre-line',
    lineHeight: 1.4,
    textAlign: 'center',
    width: '100%',
};

export const shipmentMonitoringStackCellRootSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
};

export const ensureAbsoluteUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
};

export const formatDate = (date) => {
    if (!date) return ' ';

    const d = dayjs(date);
    if (!d.isValid()) return ' ';

    return d.format('MM/DD/YY');
};

/** Resolve nested location `{ name }` or string for display. */
export const formatLocationCell = (v) => {
    if (v == null) return '';
    if (typeof v === 'object' && v.name != null) return v.name;
    return String(v);
};

export const formatAirAliasQuantityWithUnit = (alias) => {
    if (!alias || typeof alias !== 'object') return '';
    const qty = alias.quantity;
    const qtyStr = qty != null && qty !== '' ? String(qty) : '';
    const volUnit = alias.il_opus_volume_unit_id;
    let unitStr = '';
    if (volUnit != null && volUnit !== '') {
        unitStr = typeof volUnit === 'object' ? String(volUnit.name ?? volUnit.code ?? '') : String(volUnit);
    }
    const parts = [qtyStr, unitStr].filter(Boolean);
    return parts.join(' ').trim();
};

/** 그룹 키 정렬: null/빈 값은 맨 뒤 */
const monitoringStatusGroupSortKey = (v) => (v == null || v === '' ? '\uFFFF' : String(v));

const compareMonitoringStatusGroupKeys = (a, b) =>
    monitoringStatusGroupSortKey(a).localeCompare(monitoringStatusGroupSortKey(b), undefined, {
        numeric: true,
        sensitivity: 'base',
    });

/** `alias_id` 없음(null/빈 문자열)은 같은 child 안에서 먼저 오도록 정렬할 때 사용 */
export function isMonitoringIlStatusAliasKeyEmpty(ak) {
    return ak == null || ak === '';
}

function compareMonitoringAliasKeysEmptyFirst(a, b) {
    const ae = isMonitoringIlStatusAliasKeyEmpty(a);
    const be = isMonitoringIlStatusAliasKeyEmpty(b);
    if (ae && !be) return -1;
    if (!ae && be) return 1;
    if (ae && be) return 0;
    return compareMonitoringStatusGroupKeys(a, b);
}

function sortMonitoringIlStatusByEventThenId(entries) {
    return [...entries].sort((a, b) => {
        const ta = dayjs(a.event_date ?? 0).valueOf();
        const tb = dayjs(b.event_date ?? 0).valueOf();
        if (ta !== tb) return ta - tb;
        return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
}

/**
 * Status 칸의 진행 이벤트를 담당자가 읽는 순서대로 하우스 → 컨테이너 → 분할 단위로 접는다.
 * 하우스는 서류 번호가 아직 비어 있어도 서로 다른 묶음으로 남도록 번호(`house_id`)로 가르고,
 * 칸에 찍을 서류 번호는 `house_label`에서 가져온다.
 * 주의가 필요한 이벤트는 하우스 제목 바로 아래 모아 먼저 눈에 띄게 하고,
 * 나머지는 각 묶음 안에서 발생일 순으로 쌓는다.
 * @param {unknown} entries
 * @returns {{
 *   houseId: unknown;
 *   houseLabel: string;
 *   warnings: object[];
 *   children: { childId: unknown; aliases: { aliasId: unknown; entries: object[] }[] }[];
 * }[]}
 */
export function buildMonitoringIlStatusNestedGroups(entries) {
    if (!Array.isArray(entries)) return [];
    const list = entries.filter((e) => e && typeof e === 'object' && e.hidden !== true);

    const warningByHouse = new Map();
    const houseLabelByKey = new Map();
    /** @type {Map<unknown, Map<unknown, Map<unknown, object[]>>>} */
    const houseMap = new Map();

    for (const e of list) {
        const hk = e.house_id;
        if (!houseLabelByKey.has(hk)) houseLabelByKey.set(hk, e.house_label ?? '');
        if (e.warning === true) {
            if (!warningByHouse.has(hk)) warningByHouse.set(hk, []);
            warningByHouse.get(hk).push(e);
            continue;
        }
        const ck = e.child_id;
        const ak = e.alias_id;
        if (!houseMap.has(hk)) houseMap.set(hk, new Map());
        const childMap = houseMap.get(hk);
        if (!childMap.has(ck)) childMap.set(ck, new Map());
        const aliasMap = childMap.get(ck);
        if (!aliasMap.has(ak)) aliasMap.set(ak, []);
        aliasMap.get(ak).push(e);
    }

    const houseKeySet = new Set([...houseMap.keys(), ...warningByHouse.keys()]);
    const houseKeys = [...houseKeySet].sort(compareMonitoringStatusGroupKeys);

    return houseKeys
        .map((hk) => {
            const warnings = sortMonitoringIlStatusByEventThenId(warningByHouse.get(hk) ?? []);
            const childMap = houseMap.get(hk);
            const children = [];
            if (childMap) {
                // 하우스 전체에 걸린 이벤트를 개별 컨테이너 이벤트보다 위에 둔다
                const childKeys = [...childMap.keys()].sort(compareMonitoringAliasKeysEmptyFirst);
                for (const ck of childKeys) {
                    const aliasMap = childMap.get(ck);
                    const aliasKeys = [...aliasMap.keys()].sort(compareMonitoringAliasKeysEmptyFirst);
                    children.push({
                        childId: ck,
                        aliases: aliasKeys.map((ak) => ({
                            aliasId: ak,
                            entries: sortMonitoringIlStatusByEventThenId(aliasMap.get(ak)),
                        })),
                    });
                }
            }
            return { houseId: hk, houseLabel: houseLabelByKey.get(hk) ?? '', warnings, children };
        })
        .filter((g) => g.warnings.length > 0 || g.children.length > 0);
}

/** 헤더 줄: house_id / child_id / alias_id 표시용 (없으면 공백) */
export function formatMonitoringIlStatusGroupLabel(value) {
    if (value == null || value === '') return '';
    return String(value);
}

/** `event_date` → MM/DD, 메시지와 한 줄로 */
export function formatMonitoringIlStatusDisplayLine(entry) {
    if (!entry || typeof entry !== 'object') return '';
    const d = dayjs(entry.event_date);
    const mmdd = d.isValid() ? d.format('MM/DD') : '';
    const msg = entry.message != null ? String(entry.message).trim() : '';
    if (mmdd && msg) return `${mmdd} ${msg}`;
    return mmdd || msg;
}
